import * as React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Schedule as QueuedIcon,
  Error as FailedIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { withTranslation } from 'react-i18next';

// Accounting integration (Netvisor etc.) transfer status badge.
// Mirrors the MaventaStatusBadge palette/structure.
const statusConfig = {
  pending: {
    color: '#374151',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
    icon: QueuedIcon,
  },
  in_progress: {
    color: '#374151',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
    icon: QueuedIcon,
  },
  success: {
    color: '#065f46',
    bgcolor: '#d1fae5',
    borderColor: '#6ee7b7',
    icon: SuccessIcon,
  },
  failed: {
    color: '#991b1b',
    bgcolor: '#fee2e2',
    borderColor: '#fca5a5',
    icon: FailedIcon,
  },
  // The invoice IS in the accounting system, but something on it needs a human:
  // a credit note the provider would not let us link, or a counter payment that
  // did not land. Rendering these as a plain success hides work the bookkeeper
  // still has to do by hand.
  warning: {
    color: '#92400e',
    bgcolor: '#fef3c7',
    borderColor: '#fcd34d',
    icon: WarningIcon,
  },
};

const WARNING_CATEGORIES = ['unmatched_credit', 'payment_failed'];

const AccountingTransferBadge = ({ status, errorMessage, errorCategory, t, size = 'small' }) => {
  const isWarning = status === 'success' && WARNING_CATEGORIES.includes(errorCategory);
  // Only these states are worth surfacing; "", skipped etc. render nothing.
  const config = statusConfig[isWarning ? 'warning' : status];
  if (!config) {
    return null;
  }

  const IconComponent = config.icon;

  const getLabel = () => {
    if (isWarning) {
      return errorCategory === 'unmatched_credit'
        ? t('accounting.transferredUnmatchedCredit', 'Siirretty, hyvitystä ei kohdistettu')
        : t('accounting.transferredPaymentFailed', 'Siirretty, maksua ei kirjattu');
    }
    switch (status) {
      case 'pending':
      case 'in_progress':
        return t('accounting.transferQueued', 'Siirto jonossa');
      case 'success':
        return t('accounting.transferred', 'Siirretty kirjanpitoon');
      case 'failed':
        return t('accounting.transferFailed', 'Kirjanpitosiirto epäonnistui');
      default:
        return status;
    }
  };

  const chip = (
    <Chip
      label={getLabel()}
      size={size}
      icon={<IconComponent sx={{ fontSize: size === 'small' ? '14px' : '16px' }} />}
      data-testid="accounting-transfer-badge"
      sx={{
        color: config.color,
        bgcolor: config.bgcolor,
        border: `1px solid ${config.borderColor}`,
        fontWeight: 500,
        fontSize: size === 'small' ? '11px' : '12px',
        height: size === 'small' ? '22px' : '26px',
        '& .MuiChip-label': {
          px: 0.75,
        },
        '& .MuiChip-icon': {
          color: config.color,
          marginLeft: '6px',
        },
      }}
    />
  );

  // Show tooltip with the reason when something needs attention
  if ((status === 'failed' || isWarning) && errorMessage) {
    return (
      <Tooltip title={errorMessage} placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default withTranslation()(AccountingTransferBadge);
